import { Event as AppointmentIcon, Edit as EditIcon } from '@mui/icons-material'
import { useCallback, useMemo } from 'react'
import {
  ArrayField,
  Button,
  ChipField,
  Datagrid,
  DateField,
  FunctionField,
  ResourceContextProvider,
  SimpleForm,
  SingleFieldList,
  TextField,
  useDataProvider,
  useRecordContext,
  useRefresh,
} from 'react-admin'
import type { FieldValues } from 'react-hook-form'
import { Link } from 'react-router-dom'
import {
  CreateModal,
  CustomerChipField,
  Empty,
  LinkToRecord,
  SelectionField,
  UpsertAppointment,
} from '~/components'
import { appointmentRelations } from './UpsertAppointment'

export interface EditAppointmentsProps {
  /** [resourceName, recordId] of the record these appointments hang off. */
  relation: [string, string | number]
  stateCreate: [boolean, (open: boolean) => void]
}

export const EditAppointments = (props: EditAppointmentsProps) => {
  const { relation, stateCreate } = props
  const refresh = useRefresh()
  const dataProvider = useDataProvider()
  const [relName, relId] = relation

  // Opened from a boat or a customer: that one is already linked, and the
  // appointment starts at the next full hour.
  const defaultValues = useMemo(() => {
    const start = new Date()
    start.setHours(start.getHours() + 1, 0, 0, 0)
    const end = new Date(start.getTime() + 60 * 60 * 1000)
    const linked = Number(relId)
    return {
      start,
      end,
      allDay: false,
      ...(relName === 'boats' ? { boat_ids: [linked] } : {}),
      ...(relName === 'customers' ? { customer_ids: [linked] } : {}),
    }
    // A fresh start time each time the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relName, relId, stateCreate[0]])

  // This used to send an empty record (a "TODO" since 2021): the button on a
  // boat's or customer's appointment tab never created anything.
  const createAppointment = useCallback(
    async (values: FieldValues) => {
      await dataProvider.create('appointments', { data: appointmentRelations(values) })
      refresh()
      stateCreate[1](false)
    },
    [dataProvider, refresh, stateCreate]
  )

  // todo: move this somehow to editdialog and add button to datagrid
  // can't figure out how to access List from Create-, EditModal (has to be possible)
  const EditModalButton = (props: any) => {
    const { source } = props
    const record = useRecordContext(props)
    return (
      <Button
        component={Link}
        // react-admin 5 dropped linkToRecord; edit is the bare id route.
        to={`/appointments/${record?.appointment_id}`}
        // onClick={(e) => {
        //   edit(record.appointment_id)
        //   stateEditModal[1](true)
        // }}
        label='ra.action.edit'
      >
        <EditIcon />
      </Button>
    )
  }

  return (
    <>
      <ResourceContextProvider value='appointments'>
        <Empty {...props} icon={<AppointmentIcon />} resource='appointments' create={false}>
          <Datagrid rowClick={false} bulkActionButtons={false}>
            <SelectionField source='appointment.type' label='resources.appointments.fields.type' />
            <TextField source='appointment.title' label='resources.appointments.fields.title' />
            <FunctionField
              source='appointment.start'
              label='resources.appointments.fields.start'
              render={({ appointment }) => (
                <DateField source='appointment.start' showTime={!appointment.allDay} />
              )}
            />
            <FunctionField
              source='appointment.end'
              label='resources.appointments.fields.end'
              render={({ appointment }) => (
                <DateField source='appointment.end' showTime={!appointment.allDay} />
              )}
            />
            {relName !== 'customers' && (
              <ArrayField
                source='appointment.appointment_customer'
                label='resources.appointments.fields.appointment_customer'
              >
                <SingleFieldList linkType={false}>
                  <LinkToRecord resource='customers' id='customer_id'>
                    <CustomerChipField source='customer' />
                  </LinkToRecord>
                </SingleFieldList>
              </ArrayField>
            )}
            {relName !== 'boats' && (
              <ArrayField
                source='appointment.appointment_boat'
                label='resources.appointments.fields.appointment_boat'
              >
                <SingleFieldList linkType={false}>
                  <LinkToRecord resource='boats' id='boat_id'>
                    <ChipField source='boat.model' />
                  </LinkToRecord>
                </SingleFieldList>
              </ArrayField>
            )}
            <EditModalButton />
            {/* <DeleteButton redirect={false} /> */}
          </Datagrid>
        </Empty>
      </ResourceContextProvider>

      <CreateModal
        title='resources.appointments.create'
        state={stateCreate}
        {...props}
        resource='appointments'
      >
        <SimpleForm onSubmit={createAppointment} defaultValues={defaultValues} noValidate>
          <UpsertAppointment />
        </SimpleForm>
      </CreateModal>
    </>
  )
}

export default EditAppointments
